import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const OPERATING_CASH_FLOW_SERIES_META = [
  { key: "operatingCashFlow", name: "영업현금흐름", color: "#ff8c00", type: "line" },
  { key: "capitalExpenditure", name: "자본적지출", color: "#0f6fe0", type: "bar", group: "operatingCashFlow" },
  { key: "freeCashFlow", name: "잉여현금흐름", color: "#00a86b", type: "bar", group: "operatingCashFlow" },
];

const PERIOD_LIMIT = {
  quarter: 20,
  annual: 10,
};

const buildPeriodLabel = (value, period) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "");

  if (period === "annual") {
    return String(date.getFullYear());
  }

  const year = String(date.getFullYear()).slice(2);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
};

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toNumberOrZero = (value) => toNumberOrNull(value) ?? 0;

const getOperatingCashFlowBreakdown = (item) => {
  const capitalExpenditureRaw = toNumberOrNull(item?.capitalExpenditure);
  const capitalExpenditure = capitalExpenditureRaw === null ? null : Math.abs(capitalExpenditureRaw);
  const freeCashFlow = toNumberOrNull(item?.freeCashFlow);
  const operatingCashFlow = capitalExpenditure !== null || freeCashFlow !== null
    ? toNumberOrZero(capitalExpenditure) + toNumberOrZero(freeCashFlow)
    : toNumberOrNull(item?.operatingCashFlow);

  return {
    operatingCashFlow,
    capitalExpenditure,
    freeCashFlow,
  };
};

export default function OperatingCashFlowBreakdownChart({ ticker, selectedPeriod = "quarter", chartDataByPeriod = { quarter: null, annual: null }, isLoading = false }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isChartLoading = Boolean(ticker && (isLoading || chartDataByPeriod[selectedPeriod] === null));

  const rows = useMemo(
    () => (Array.isArray(chartDataByPeriod[selectedPeriod]) ? [...chartDataByPeriod[selectedPeriod]] : [])
      .filter((item) => item?.date)
      .map((item) => ({
        ...item,
        operatingCashFlowBreakdown: getOperatingCashFlowBreakdown(item),
      }))
      .filter((item) => OPERATING_CASH_FLOW_SERIES_META.some(({ key }) => toNumberOrNull(item?.operatingCashFlowBreakdown?.[key]) !== null))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-(PERIOD_LIMIT[selectedPeriod] ?? 20)),
    [chartDataByPeriod, selectedPeriod]
  );

  const categories = useMemo(
    () => rows.map((item, index) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return buildPeriodLabel(item.date, selectedPeriod);

      const currentYear = date.getFullYear();
      if (selectedPeriod === "annual") {
        return String(currentYear);
      }

      const previousRow = index > 0 ? rows[index - 1] : null;
      const previousYear = previousRow?.date ? new Date(previousRow.date).getFullYear() : null;
      return currentYear !== previousYear ? String(currentYear) : "";
    }),
    [rows, selectedPeriod]
  );

  const series = useMemo(
    () => OPERATING_CASH_FLOW_SERIES_META.map(({ key, name, type, group }) => ({
      name,
      type,
      group,
      data: rows.map((item) => toNumberOrNull(item?.operatingCashFlowBreakdown?.[key]) ?? 0),
    })),
    [rows]
  );

  const hasData = useMemo(
    () => series.some((entry) => entry.data.some((value) => value !== 0 && value !== null)),
    [series]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        stackOnlyBar: true,
        toolbar: { show: false },
        animations: { enabled: false },
        background: chartColors.panelBg,
      },
      colors: OPERATING_CASH_FLOW_SERIES_META.map((item) => item.color),
      responsive: [
        {
          breakpoint: 576,
          options: {
            xaxis: {
              labels: {
                rotate: -90,
                rotateAlways: true,
              },
            },
          },
        },
      ],
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 0.1,
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "58%",
          borderRadius: 3,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: OPERATING_CASH_FLOW_SERIES_META.map(({ type }) => (type === "line" ? 3 : 0)),
        curve: "straight",
      },
      markers: {
        size: [0, 0, 0],
        hover: {
          size: 4,
        },
      },
      fill: {
        opacity: OPERATING_CASH_FLOW_SERIES_META.map(({ type, key }) => {
          if (type === "line") return 1;
          if (key === "capitalExpenditure") return 0.62;
          return 1;
        }),
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        clusterGroupedSeries: false,
        clusterGroupedSeriesOrientation: "horizontal",
        fontSize: isMobile ? "11px" : "14px",
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
        labels: {
          colors: chartColors.legendColor,
        },
      },
      title: {
        text: "현금흐름 세부내역",
        align: "center",
        offsetY: 10,
        style: {
          color: chartColors.legendColor,
          fontSize: "18px",
          fontWeight: 700,
        },
      },
      grid: {
        borderColor: chartColors.gridColor,
        strokeDashArray: 4,
        padding: {
          top: 2,
          bottom: 2,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
          formatter: (value) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000_000).toFixed(1).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}T`;
            }
            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}M`;
            }
            if (absValue >= 1_000) {
              return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}K`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      tooltip: {
        theme: isDarkMode ? "dark" : "light",
        shared: true,
        intersect: false,
        x: {
          formatter: (_, context) => rows?.[context.dataPointIndex]?.date || "",
        },
        y: {
          formatter: (value) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000_000).toFixed(2).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}T`;
            }
            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(2).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(2).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}M`;
            }
            if (absValue >= 1_000) {
              return `${sign}$${(absValue / 1_000).toFixed(2).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1")}K`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      noData: {
        text: "데이터가 없습니다.",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: chartColors.tickColor,
        },
      },
    }),
    [categories, chartColors, isDarkMode, isMobile, rows]
  );

  if (isChartLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderRadius: "12px", color: chartColors.tickColor }}
      >
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <Card
        className="d-flex align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
      >
        데이터가 없습니다.
      </Card>
    );
  }

  return (
    <Card
      className="w-full h-full"
      style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
    >
      <Chart options={options} series={series} type="bar" height={300} />
    </Card>
  );
}
